// @ts-nocheck
import { useEffect, useState } from 'react';
import { ChevronRight, Lock, Shield } from 'lucide-react';
import { StatusBadge } from '../shared/StatusBadge';
import { fmtDuration, pctFmt } from '../shared/voteRoomFormat';
import { getShareholderVoteStatus } from '../shared/voteRoomVoteStatus';
import { SignConsentModal } from './SignConsentModal';

const initialVoteFromStatus = (status) =>
  status === 'approved' ? 'approve' : status === 'disapproved' ? 'disapprove' : null;

const isSignedStatus = (status) => status === 'approved' || status === 'disapproved';

/**
 * Shareholder's view of the consent process: scroll-to-read Disclosure → Consent → execute via
 * DocuSign. Rendered both standalone (future production use) and inside the admin preview slide-over
 * (`compactHeader` trims the header detail line then).
 */
export const ShareholderPortal = ({
  shareholder,
  onBack,
  onSign,
  voteRoomClosed,
  companyName,
  compactHeader = false,
  readOnlyPreview = false,
}) => {
  const [curDoc, setCurDoc] = useState(0);
  const [discRead, setDiscRead] = useState(false);
  const [consRead, setConsRead] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(isSignedStatus(shareholder.status));
  const [vote, setVote] = useState(initialVoteFromStatus(shareholder.status));
  const [timer, setTimer] = useState(0);
  const voteStatus = getShareholderVoteStatus(shareholder, { voteRoomClosed, signed, vote });

  useEffect(() => {
    setSigned(isSignedStatus(shareholder.status));
    setVote(initialVoteFromStatus(shareholder.status));
  }, [shareholder.id, shareholder.status]);

  useEffect(() => {
    const i = setInterval(() => setTimer((t) => t + 1), 1000);
    return () => clearInterval(i);
  }, []);

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 30) {
      if (curDoc === 0) setDiscRead(true);
      if (curDoc === 1) setConsRead(true);
    }
  };

  const handleSign = (c) => {
    if (readOnlyPreview || !onSign) return;
    setVote(c);
    setSigning(true);
    setTimeout(() => {
      setSigning(false);
      setSigned(true);
      onSign(shareholder.id, c);
    }, 1800);
  };

  const docs = [
    {
      id: 'disclosure',
      title: 'Disclosure Statement',
      content: `SECTION 280G SHAREHOLDER DISCLOSURE STATEMENT\n${companyName}`,
    },
    {
      id: 'consent',
      title: 'Shareholder Consent',
      content: `SHAREHOLDER WRITTEN CONSENT IN LIEU OF A MEETING\n${companyName}`,
    },
  ];

  return (
    <div className="space-y-4">
      {readOnlyPreview ? (
        <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2.5 text-xs text-blue-800">
          Preview only — signing is disabled in admin view-as mode.
        </div>
      ) : null}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-gray-400 hover:text-gray-600 flex items-center space-x-1"
        >
          <ChevronRight size={10} className="rotate-180" />
          <span>Back to Admin</span>
        </button>
      )}
      <div className="bg-white border border-gray-200/80 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 text-xs font-semibold text-gray-900">
            280G Shareholder Consent — {companyName}
            <div className="text-xs font-normal text-gray-400 mt-0.5">
              {compactHeader ? (
                <>Session: {fmtDuration(timer)}</>
              ) : (
                <>
                  {shareholder.name} · {shareholder.shares.toLocaleString()} shares ({pctFmt(shareholder.pct || 0)}) ·
                  Session: {fmtDuration(timer)}
                </>
              )}
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <StatusBadge variant="secure">
              <Lock size={8} className="mr-1" />
              Secure
            </StatusBadge>
            {!compactHeader ? <StatusBadge variant={voteStatus}>{voteStatus}</StatusBadge> : null}
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-1 bg-gray-100/80 rounded-lg p-0.5 w-fit">
        {docs.map((d, i) => (
          <button
            type="button"
            key={d.id}
            onClick={() => setCurDoc(i)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
              curDoc === i ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {d.title}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200/80 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden">
        <div onScroll={handleScroll} className="px-10 py-8 max-h-[320px] overflow-y-auto relative">
          <pre className="text-xs leading-relaxed whitespace-pre-wrap text-gray-700">{docs[curDoc].content}</pre>
        </div>
        <div className="px-6 py-2.5 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
          <span className="text-xs text-gray-400">
            {curDoc === 0 && !discRead
              ? '↓ Scroll to bottom'
              : curDoc === 1 && !consRead
                ? '↓ Scroll to bottom'
                : '✓ Reviewed'}
          </span>
          <div className="flex items-center space-x-2">
            {curDoc === 0 && (
              <button
                type="button"
                onClick={() => setCurDoc(1)}
                disabled={!discRead}
                className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-[#0f172a] text-white hover:bg-[#1e293b] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Consent
                <ChevronRight size={10} className="ml-1" />
              </button>
            )}
            {curDoc === 1 && !signed && !voteRoomClosed && !readOnlyPreview && (
              <button
                type="button"
                onClick={() => setShowConsent(true)}
                disabled={!discRead || !consRead}
                className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Shield size={10} className="mr-1.5" />
                Execute via DocuSign
              </button>
            )}
          </div>
        </div>
      </div>

      <SignConsentModal
        open={showConsent && !signed}
        signing={signing}
        onApprove={() => handleSign('approve')}
        onDisapprove={() => handleSign('disapprove')}
        onClose={() => setShowConsent(false)}
      />
    </div>
  );
};
